import { createContext, useContext } from 'react';

export type TableMetricContext = {
  /** Table identifier from data-cy prop. Omitted if not provided. */
  table_id?: string;
  /** Number of rows currently displayed */
  row_count: number;
  /** Current page size (items per page) */
  page_size: number | null;
  /** Current page index (0-based) */
  page_index: number | null;
  /** Whether a search term is active (NOT the term itself) */
  has_search_term: boolean;
  /** Number of active filters (NOT their values) */
  filter_count: number;
  /** Whether the table is currently sorted */
  is_sorted: boolean;
  /** Whether the table is loading data */
  is_loading: boolean;
};

export type PendingInteraction = {
  type: string;
  startTime: number;
  context?: Record<string, unknown>;
};

export type TableInstrumentationContextValue = {
  trackAction: (name: string, context?: Record<string, unknown>) => void;
  trackTiming: (
    name: string,
    durationMs: number,
    context?: Record<string, unknown>,
  ) => void;
  trackError: (error: unknown, context?: Record<string, unknown>) => void;
  getContext: () => TableMetricContext;

  /**
   * Mark the start of a user interaction (e.g., sort, page change).
   * Call this in the event handler *before* triggering the state change
   * so the timer captures the full reconciliation + paint cost.
   */
  markInteraction: (type: string, context?: Record<string, unknown>) => void;

  consumePendingInteraction: () => PendingInteraction | null;
};

const TableInstrumentationContext =
  createContext<TableInstrumentationContextValue | null>(null);

export const { Provider: InstrumentationContextProvider } =
  TableInstrumentationContext;

export const useTableInstrumentationMaybe =
  (): TableInstrumentationContextValue | null =>
    useContext(TableInstrumentationContext);
